import { FolderOpen, Plus, Trash2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ThumbnailGrid } from './DropZoneThumbnail';

const SUPPORTED_TYPES = new Set(['image/jpeg', 'image/png']);

export interface DropZoneProps {
  readonly files: readonly File[];
  readonly heroIndex: number;
  /**
   * Wave 22: ファイル追加コールバック。既存に append される。
   * 呼び出し側は addFiles() を呼ぶこと (setFiles だと上書きになり、追加機能が壊れる)。
   */
  readonly onFilesAdded: (files: File[]) => void;
  readonly onHeroChange: (index: number) => void;
  readonly onFileRemoved: (index: number) => void;
  readonly onClearAll: () => void;
  readonly disabled?: boolean;
}

export function DropZone(props: DropZoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const { onFilesAdded, disabled = false } = props;

  const accept = useCallback(
    (fileList: FileList | File[]) => {
      const next: File[] = [];
      for (const f of Array.from(fileList)) {
        if (SUPPORTED_TYPES.has(f.type)) next.push(f);
      }
      if (next.length > 0) onFilesAdded(next);
    },
    [onFilesAdded],
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragActive(false);
      if (disabled) return;
      accept(e.dataTransfer.files);
    },
    [accept, disabled],
  );

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const dropHandlers = { onDragOver, onDragLeave, onDrop };

  if (props.files.length === 0) {
    return (
      <EmptyDropZone
        dragActive={dragActive}
        disabled={disabled}
        dropHandlers={dropHandlers}
        onOpen={openPicker}
        inputRef={inputRef}
        accept={accept}
      />
    );
  }

  return (
    <FilledDropZone
      files={props.files}
      heroIndex={props.heroIndex}
      disabled={disabled}
      dragActive={dragActive}
      dropHandlers={dropHandlers}
      onHeroChange={props.onHeroChange}
      onAdd={openPicker}
      onFileRemoved={props.onFileRemoved}
      onClearAll={props.onClearAll}
      inputRef={inputRef}
      accept={accept}
    />
  );
}

// ===== サブコンポーネント ================================================

interface DropHandlers {
  readonly onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  readonly onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  readonly onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}

interface EmptyZoneProps {
  readonly dragActive: boolean;
  readonly disabled: boolean;
  readonly dropHandlers: DropHandlers;
  readonly onOpen: () => void;
  readonly inputRef: React.RefObject<HTMLInputElement>;
  readonly accept: (files: FileList | File[]) => void;
}

function EmptyDropZone(props: EmptyZoneProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex h-80 flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed transition-colors',
        props.dragActive
          ? 'border-accent-primary bg-[rgba(10,37,64,0.05)]'
          : 'border-silver-primary hover:border-accent-primary hover:bg-[rgba(10,37,64,0.03)]',
        props.disabled && 'pointer-events-none opacity-50',
      )}
      {...props.dropHandlers}
    >
      <FolderOpen className="text-text-secondary h-12 w-12" strokeWidth={1.5} aria-hidden />
      <div className="space-y-1 text-center">
        <p className="text-text-primary text-base font-medium">車両写真をドロップ</p>
        <p className="text-text-secondary text-xs">
          または{' '}
          <button
            type="button"
            className="text-accent-primary hover:underline"
            onClick={props.onOpen}
          >
            クリックして選択
          </button>{' '}
          (JPEG / PNG、最大 80 枚、複数回に分けて追加可能)
        </p>
      </div>
      <input
        ref={props.inputRef}
        type="file"
        accept="image/jpeg,image/png"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) props.accept(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

interface FilledZoneProps {
  readonly files: readonly File[];
  readonly heroIndex: number;
  readonly disabled: boolean;
  readonly dragActive: boolean;
  readonly dropHandlers: DropHandlers;
  readonly onHeroChange: (index: number) => void;
  readonly onAdd: () => void;
  readonly onFileRemoved: (index: number) => void;
  readonly onClearAll: () => void;
  readonly inputRef: React.RefObject<HTMLInputElement>;
  readonly accept: (files: FileList | File[]) => void;
}

function FilledDropZone(props: FilledZoneProps): JSX.Element {
  const full = props.files.length >= 80;
  return (
    <div
      className={cn(
        'border-silver-primary bg-bg-primary space-y-3 rounded-lg border p-4 transition-colors',
        props.dragActive && 'border-accent-primary bg-[rgba(10,37,64,0.04)]',
      )}
      {...props.dropHandlers}
    >
      <FilledHeader
        count={props.files.length}
        heroName={props.files[props.heroIndex]?.name ?? '-'}
        full={full}
        disabled={props.disabled}
        onAdd={props.onAdd}
        onClearAll={props.onClearAll}
      />
      <ThumbnailGrid
        files={props.files}
        heroIndex={props.heroIndex}
        disabled={props.disabled}
        onHeroChange={props.onHeroChange}
        onFileRemoved={props.onFileRemoved}
      />
      {!full ? (
        <p className="text-text-tertiary border-silver-tertiary border-t pt-2 text-[11px]">
          さらに写真をドロップすると追加されます (現在 {props.files.length}/80 枚)
        </p>
      ) : (
        <p className="text-status-warning border-silver-tertiary border-t pt-2 text-[11px]">
          上限 80 枚に達しています
        </p>
      )}
      <input
        ref={props.inputRef}
        type="file"
        accept="image/jpeg,image/png"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) props.accept(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function FilledHeader(props: {
  readonly count: number;
  readonly heroName: string;
  readonly full: boolean;
  readonly disabled: boolean;
  readonly onAdd: () => void;
  readonly onClearAll: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="text-sm">
        <span className="text-text-primary font-medium tabular-nums">{props.count}</span>
        <span className="text-text-secondary"> / 80 枚</span>
        <span className="text-text-tertiary ml-3 text-xs">
          ヒーロー: <span className="text-text-secondary">{props.heroName}</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={props.onAdd}
          disabled={props.disabled || props.full}
          type="button"
        >
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          写真を追加
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={props.onClearAll}
          disabled={props.disabled}
          type="button"
        >
          <Trash2 className="mr-1 h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          全てクリア
        </Button>
      </div>
    </div>
  );
}
